/**
 * `<AnalysisCards>` — four-card output stack for the post-interview Analysis tab (B-13).
 *
 * Matches prototype `renderAnalysisCards(text, 'analysisSelect')` (L636).
 *
 * Card order (prototype L784-802):
 *   1. `<VerdictCard>`             — Post-Interview Verdict (always if recKey found)
 *   2. `<OverallAssessmentCard>`   — collapsed; SILENT SKIP when no overview/overall
 *                                    assessment key in sections (proto L646 `overviewAsCard`)
 *   3. `<ScorecardGrid>`           — Interview Scorecard (shown if ≥1 rows)
 *   4. `<DetailedAssessmentSplit>` — nav + detail pane
 *
 * Backend currently emits `## Bottom Line` (maps to VerdictCard via
 * /recommendation|bottom line/i). It does NOT emit `## Overall Assessment`
 * or `## Overview`, so `<OverallAssessmentCard>` will silently not render.
 * Architect decision §6 (b): frontend-only, no prompt change this batch.
 *
 * Fallback: when no sections parsed at all, render the raw analysis via
 * `<EvaluationMarkdown>` in a single card (matches prototype L663 fallback).
 *
 * B-13 Δ2: VerdictCard now accepts `rankBefore`/`rankAfter` for the
 * RankDeltaBadge wiring (Q3 ruling).
 */
import type { JSX } from "react";
import { CollapsibleCard } from "@/components/common/CollapsibleCard";
import { EvaluationMarkdown } from "@/components/candidate-page/EvaluationMarkdown";
import {
  parseScorecardRows,
  parseEvalSections,
} from "@/lib/interview-analysis/parsers";
import { VerdictCard } from "./VerdictCard";
import { ScorecardGrid } from "./ScorecardGrid";
import { DetailedAssessmentSplit } from "./DetailedAssessmentSplit";

interface AnalysisCardsProps {
  /** Full raw analysis markdown string. */
  analysis: string;
  /** Rank before analysis (forwarded to VerdictCard → RankDeltaBadge). */
  rankBefore?: number | null;
  /** Rank after analysis (forwarded to VerdictCard → RankDeltaBadge). */
  rankAfter?: number | null;
}

export function AnalysisCards({
  analysis,
  rankBefore,
  rankAfter,
}: AnalysisCardsProps): JSX.Element {
  const scorecardRows = parseScorecardRows(analysis);
  const allSections = parseEvalSections(analysis);

  // Key detection — mirrors prototype L643-650.
  const allKeys = Object.keys(allSections);
  const recKey = allKeys.find((k) => /recommendation|bottom line/i.test(k));
  const overviewKey = allKeys.find((k) => /overview|overall assessment/i.test(k));

  // For post-interview (analysisSelect path): overview goes as its own card,
  // NOT as a nav item. Prototype L646: `overviewAsCard = isPost && overviewKey`.
  const overviewAsCard = !!overviewKey;

  // Drill keys: exclude recKey + overviewKey (overviewKey handled as card above).
  const drillKeys = allKeys.filter((k) => {
    if (k === recKey) return false;
    if (k === overviewKey && overviewAsCard) return false;
    return true;
  });

  const drillSections: Record<string, string> = {};
  drillKeys.forEach((k) => {
    drillSections[k] = allSections[k] ?? "";
  });

  // Fallback: no sections at all → single EvaluationMarkdown block.
  if (!allKeys.length && !scorecardRows.length) {
    return (
      <div className="space-y-3" data-testid="analysis-cards">
        <CollapsibleCard title="Analysis" defaultOpen={true}>
          <EvaluationMarkdown source={analysis} />
        </CollapsibleCard>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="analysis-cards">
      {/* Card 1 — Post-Interview Verdict (recKey found) */}
      {recKey && allSections[recKey] ? (
        <VerdictCard
          recommendationBody={allSections[recKey]}
          rankBefore={rankBefore}
          rankAfter={rankAfter}
        />
      ) : null}

      {/*
       * Card 2 — Overall Assessment card.
       * SILENT SKIP when `overviewKey` is undefined (backend doesn't emit
       * `## Overall Assessment` or `## Overview` in current INTERVIEW_ANALYSIS_POST_SYSTEM
       * prompt — Architect §6 decision (b): frontend-only skip, no prompt change).
       * When present (prototype L666-676), render collapsed with EvaluationMarkdown body.
       */}
      {overviewAsCard && overviewKey && allSections[overviewKey] ? (
        <CollapsibleCard title="Overall Assessment" defaultOpen={false}>
          <EvaluationMarkdown source={allSections[overviewKey]} />
        </CollapsibleCard>
      ) : null}

      {/* Card 3 — Interview Scorecard */}
      {scorecardRows.length > 0 ? (
        <ScorecardGrid rows={scorecardRows} />
      ) : null}

      {/* Card 4 — Detailed Assessment */}
      {Object.keys(drillSections).length > 0 ? (
        <DetailedAssessmentSplit sections={drillSections} />
      ) : null}
    </div>
  );
}
